/**
 * Pantalla 3 (docs/FASE-1.md §6): endpoints de webhook de un módulo, alcanzados desde
 * `ModuleDetail`. Alta, prueba, rotar secreto y eliminar; el secreto se muestra una única vez.
 */
import { WEBHOOK_EVENTS, type CreateWebhookEndpointInput, type WebhookEventType, type WebhookEndpoint } from "@continnua/contracts";
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";

import { webhooksApi } from "../api/client";
import { Badge, toneForStatus } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { SecretReveal } from "../components/SecretReveal";
import { toast } from "../components/Toast";
import { toDisplayMessage } from "../lib/errors";

function CreateWebhookModal({
  moduleCode,
  onClose,
  onCreated,
}: {
  moduleCode: string;
  onClose: () => void;
  onCreated: (endpoint: WebhookEndpoint, secret: string) => void;
}): ReactElement {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<Set<WebhookEventType>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function toggleEvent(event: WebhookEventType, checked: boolean): void {
    setEvents((prev) => {
      const next = new Set(prev);
      if (checked) next.add(event);
      else next.delete(event);
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateWebhookEndpointInput = { url, events: [...events] };
      const created = await webhooksApi.create(moduleCode, input);
      const { secret, ...endpoint } = created;
      onCreated(endpoint, secret);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Nuevo endpoint de webhook" onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="URL" hint="Debe aceptar peticiones POST; se valida contra IPs privadas.">
          {(props) => <input {...props} type="url" required value={url} onChange={(e) => setUrl(e.target.value)} />}
        </Field>
        <fieldset className="roles-fieldset">
          <legend>Eventos</legend>
          {WEBHOOK_EVENTS.map((eventType) => {
            const id = `webhook-event-${eventType}`;
            return (
              <label key={eventType} className="checkbox-label" htmlFor={id}>
                <input id={id} type="checkbox" checked={events.has(eventType)} onChange={(e) => toggleEvent(eventType, e.target.checked)} />
                {eventType}
              </label>
            );
          })}
        </fieldset>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting || events.size === 0}>
            {submitting ? "Creando…" : "Crear endpoint"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ModuleWebhooks({ moduleCode }: { moduleCode: string }): ReactElement {
  const [items, setItems] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<WebhookEndpoint | null>(null);
  const [revealSecret, setRevealSecret] = useState<{ title: string; secret: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await webhooksApi.list(moduleCode);
      setItems(page.items);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  }, [moduleCode]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleTest(endpointId: string): Promise<void> {
    setBusyId(endpointId);
    try {
      const delivery = await webhooksApi.test(moduleCode, endpointId);
      toast.info(`Entrega de prueba encolada (${delivery.status}).`);
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleRotate(endpointId: string): Promise<void> {
    setBusyId(endpointId);
    try {
      const created = await webhooksApi.rotateSecret(moduleCode, endpointId);
      const { secret, ...endpoint } = created;
      setItems((prev) => prev.map((e) => (e.id === endpoint.id ? endpoint : e)));
      setRevealSecret({ title: "Nuevo secreto del endpoint", secret });
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  const columns: DataTableColumn<WebhookEndpoint>[] = [
    { key: "url", header: "URL", render: (e) => e.url },
    { key: "events", header: "Eventos", render: (e) => e.events.join(", ") },
    { key: "status", header: "Estado", render: (e) => <Badge tone={toneForStatus(e.status)}>{e.status === "active" ? "Activo" : "Deshabilitado"}</Badge> },
    {
      key: "actions",
      header: "Acciones",
      render: (e) => (
        <div className="button-row">
          <button type="button" className="button-secondary button-small" disabled={busyId === e.id} onClick={() => void handleTest(e.id)}>
            Probar
          </button>
          <button type="button" className="button-secondary button-small" disabled={busyId === e.id} onClick={() => void handleRotate(e.id)}>
            Rotar secreto
          </button>
          <button type="button" className="button-danger button-small" onClick={() => setRemoving(e)}>
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  return (
    <section>
      <div className="page-header">
        <h2>Webhooks</h2>
        <button type="button" className="button-primary" onClick={() => setShowCreate(true)}>
          Nuevo endpoint
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <DataTable columns={columns} rows={items} rowKey={(e) => e.id} emptyMessage={loading ? "Cargando…" : "No hay endpoints de webhook."} />
      {showCreate && (
        <CreateWebhookModal
          moduleCode={moduleCode}
          onClose={() => setShowCreate(false)}
          onCreated={(endpoint, secret) => {
            setShowCreate(false);
            setItems((prev) => [endpoint, ...prev]);
            setRevealSecret({ title: "Secreto del endpoint", secret });
          }}
        />
      )}
      {revealSecret && <SecretReveal title={revealSecret.title} secret={revealSecret.secret} onClose={() => setRevealSecret(null)} />}
      {removing && (
        <ConfirmDialog
          title="Eliminar endpoint de webhook"
          message={`¿Eliminar el endpoint ${removing.url}? Dejará de recibir eventos.`}
          confirmLabel="Eliminar"
          danger
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await webhooksApi.remove(moduleCode, removing.id);
              setItems((prev) => prev.filter((e) => e.id !== removing.id));
              setRemoving(null);
              toast.success("Endpoint eliminado.");
            } catch (err) {
              toast.error(toDisplayMessage(err));
            }
          }}
        />
      )}
    </section>
  );
}
